import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import SEO, { serviceSchema, faqSchema, breadcrumbSchema } from "@/components/SEO";
import {
  GraduationCap,
  Check,
  X,
  ArrowRight,
  Brain,
  Target,
  BarChart3,
  Globe,
  Sparkles,
  Users,
  Clock,
  Shield,
  MessageCircle,
  Loader2,
  Zap,
  Trophy,
  Crown,
  Download,
  FileText,
  Percent,
  UserPlus,
} from "lucide-react";
import html2pdf from "html2pdf.js";

// 11 supported languages
const SUPPORTED_LANGUAGES = [
  "English", "Hindi", "Tamil", "Telugu", "Kannada", 
  "Marathi", "Punjabi", "Bengali", "Gujarati", "Malayalam", "Odia"
];

// Default plans with detailed features (matching Pricing.js)
const DEFAULT_PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 499,
    duration: "child/subject/month",
    dailyLimit: "1 hour",
    practiceTests: "1/day",
    description: "Perfect for getting started with AI-powered learning",
    icon: Zap,
    color: "emerald",
    bgGradient: "from-emerald-500 to-teal-600",
    popular: false,
    features: [
      { name: "WhatsApp Learning", included: true },
      { name: "AI-Powered Lessons", included: true },
      { name: "Basic Progress Tracking", included: true },
      { name: "1 Practice Test/day", included: true },
      { name: "Parent Dashboard (Basic)", included: true },
      { name: "Last 3 Sessions History", included: true },
      { name: "Weekly Email Summary", included: true },
    ],
    notIncluded: [
      "Leaderboard Access",
      "Certificates",
      "Study Plans",
    ],
    cta: "Get Started",
    ctaVariant: "outline",
  },
  {
    id: "plus",
    name: "Plus",
    price: 699,
    duration: "child/subject/month",
    dailyLimit: "2 hours",
    practiceTests: "3/day",
    description: "Enhanced learning with tests, certificates & reports",
    icon: Trophy,
    color: "violet",
    bgGradient: "from-violet-500 to-purple-600",
    popular: true,
    features: [
      { name: "Everything in Starter", included: true, highlight: true },
      { name: "2 hours/day learning", included: true },
      { name: "3 Practice Tests/day", included: true, highlight: true },
      { name: "Leaderboard Access", included: true },
      { name: "Weekly Progress Reports", included: true },
      { name: "Progress Certificates", included: true, highlight: true },
      { name: "5 Achievement Badges", included: true },
      { name: "Last 10 Sessions History", included: true },
    ],
    notIncluded: [
      "Full Study Plans",
      "Percentile Rankings",
    ],
    cta: "Choose Plus",
    ctaVariant: "default",
  },
  {
    id: "pro",
    name: "Pro",
    price: 899,
    duration: "child/subject/month",
    dailyLimit: "4 hours",
    practiceTests: "Unlimited",
    description: "Complete learning suite with AI study plans & analytics",
    icon: Crown,
    color: "amber",
    bgGradient: "from-amber-500 to-orange-600",
    popular: false,
    features: [
      { name: "Everything in Plus", included: true, highlight: true },
      { name: "Unlimited Practice Tests", included: true, highlight: true },
      { name: "AI Study Plans", included: true, highlight: true },
      { name: "Percentile Rankings", included: true },
      { name: "Daily Progress Reports", included: true },
      { name: "All Achievement Badges", included: true, highlight: true },
      { name: "Full Session History", included: true },
      { name: "Priority Support", included: true },
    ],
    cta: "Go Pro",
    ctaVariant: "outline",
  },
];

// Icon mapping for dynamic plans
const ICON_MAP = {
  starter: Zap,
  plus: Trophy,
  pro: Crown,
};

const COLOR_MAP = {
  starter: { color: "emerald", bgGradient: "from-emerald-500 to-teal-600" },
  plus: { color: "violet", bgGradient: "from-violet-500 to-purple-600" },
  pro: { color: "amber", bgGradient: "from-amber-500 to-orange-600" },
};

export default function EduWise() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [freeTrialDays, setFreeTrialDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const brochureRef = useRef(null);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API}/admin/public/pricing`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.plans) {
          // Merge dynamic pricing with our UI config
          const mergedPlans = data.plans.map((plan) => {
            const defaultPlan = DEFAULT_PLANS.find(p => p.id === plan.id) || {};
            return {
              ...defaultPlan,
              ...plan,
              icon: ICON_MAP[plan.id] || Zap,
              ...(COLOR_MAP[plan.id] || COLOR_MAP.starter),
              cta: plan.id === "starter" ? "Get Started" : plan.id === "plus" ? "Choose Plus" : "Go Pro",
              ctaVariant: plan.id === "plus" ? "default" : "outline",
            };
          });
          setPlans(mergedPlans);
          setFreeTrialDays(data.free_trial_days || 90);
        }
      }
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planId) => {
    navigate(`/login?register=true&plan=${planId}`);
  };

  const generatePDF = async () => {
    setGeneratingPdf(true);
    
    // Create brochure HTML content
    const brochureContent = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #10b981, #14b8a6, #06b6d4); color: white; padding: 30px; text-align: center; border-radius: 12px; margin-bottom: 20px;">
          <h1 style="font-size: 28px; margin: 0 0 8px 0;">Eduzy EduWise</h1>
          <p style="margin: 0; opacity: 0.9;">Your Child's Personal AI Tutor</p>
          <h2 style="font-size: 20px; margin: 15px 0 5px 0;">Subscription Plans Comparison</h2>
        </div>
        
        <div style="background: #f0fdf4; padding: 15px; border-radius: 8px; text-align: center; margin-bottom: 20px; border: 1px solid #bbf7d0;">
          <p style="margin: 0; color: #059669; font-weight: 600;">🎓 ${freeTrialDays}-Day Free Trial on All Plans</p>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <thead>
            <tr style="background: #f9fafb;">
              <th style="text-align: left; padding: 12px; border-bottom: 2px solid #e5e7eb; width: 40%;">Feature</th>
              <th style="text-align: center; padding: 12px; border-bottom: 2px solid #e5e7eb; background: linear-gradient(135deg, #10b981, #14b8a6); color: white;">Starter<br/>₹${plans[0]?.price || 599}/mo</th>
              <th style="text-align: center; padding: 12px; border-bottom: 2px solid #e5e7eb; background: linear-gradient(135deg, #8b5cf6, #a855f7); color: white;">Plus<br/>₹${plans[1]?.price || 799}/mo</th>
              <th style="text-align: center; padding: 12px; border-bottom: 2px solid #e5e7eb; background: linear-gradient(135deg, #f59e0b, #f97316); color: white;">Pro<br/>₹${plans[2]?.price || 899}/mo</th>
            </tr>
          </thead>
          <tbody>
            <tr style="background: #ecfdf5;"><td colspan="4" style="padding: 8px 12px; font-weight: 700; color: #059669;">📚 Core Learning Features</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">WhatsApp Learning</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">AI-Powered Lessons</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Curriculum-Aligned Content (CBSE / IB)</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">11 Indian Languages Support</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Adaptive Learning (AI adjusts to pace)</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            
            <tr style="background: #ecfdf5;"><td colspan="4" style="padding: 8px 12px; font-weight: 700; color: #059669;">⏰ Daily Learning Limits</td></tr>
            <tr style="background: #fef3c7;"><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Daily Learning Time</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">60 minutes</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">120 minutes</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">240 minutes</td></tr>
            <tr style="background: #fef3c7;"><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Practice Tests per Day</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">1 test</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">3 tests</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">Unlimited</td></tr>
            
            <tr style="background: #ecfdf5;"><td colspan="4" style="padding: 8px 12px; font-weight: 700; color: #059669;">📊 Progress Tracking & Reports</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Basic Progress Tracking</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Session History</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">Last 3</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">Last 10</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">Full History</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Weekly Email Summary</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Weekly Progress Reports</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Daily Progress Reports</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Quarterly Performance Reports</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            
            <tr style="background: #ecfdf5;"><td colspan="4" style="padding: 8px 12px; font-weight: 700; color: #059669;">👨‍👩‍👧 Parent Dashboard</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Parent Dashboard Access</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">View Only</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">Full Access</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">Full Access</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Learning Insights</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Set Learning Goals</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            
            <tr style="background: #ecfdf5;"><td colspan="4" style="padding: 8px 12px; font-weight: 700; color: #059669;">🏆 Gamification & Recognition</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Leaderboard Access</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Achievement Badges</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">5 Badges</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; font-weight: 600;">All Badges</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Progress Certificates</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Percentile Rankings</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            
            <tr style="background: #ecfdf5;"><td colspan="4" style="padding: 8px 12px; font-weight: 700; color: #059669;">🚀 Advanced Features</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">AI Study Plans</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Personalized Learning Path</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
            <tr><td style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">Priority Support</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #d1d5db;">—</td><td style="text-align: center; padding: 10px; border-bottom: 1px solid #f3f4f6; color: #10b981; font-weight: bold;">✓</td></tr>
          </tbody>
        </table>
        
        <div style="background: #f9fafb; padding: 20px; text-align: center; margin-top: 20px; border-radius: 8px; border: 1px solid #e5e7eb;">
          <p style="color: #6b7280; font-size: 11px; margin: 0 0 10px 0;">All prices are per subject per month. GST applicable as per government norms.</p>
          <p style="color: #6b7280; font-size: 11px; margin: 0 0 10px 0;">Subjects: Mathematics, Science, English, Hindi</p>
          <p style="color: #9ca3af; font-size: 10px; margin: 0;">Questions? Contact us at support@eduzy.co | www.eduzy.co</p>
        </div>
      </div>
    `;
    
    const opt = {
      margin: 10,
      filename: 'EduWise-Plans-Comparison.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    try {
      const element = document.createElement('div');
      element.innerHTML = brochureContent;
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('PDF generation failed:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Structured data for EduWise service
  const eduWiseStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      serviceSchema({
        name: "EduWise - AI Tutor",
        description:
          "Personalized AI tutoring platform for grades 5-8. Adaptive learning that adjusts to your child's pace and style. Curriculum-aligned content in 11 Indian languages.",
        price: plans[0]?.price?.toString() || "499",
      }),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "EduWise AI Tutor", url: "/eduwise" },
      ]),
      faqSchema([
        {
          question: "What is EduWise AI Tutor?",
          answer:
            "EduWise is a personalized AI tutoring platform that adapts to your child's learning pace and style. It provides curriculum-aligned lessons for grades 5-8 in Math, Science, English, and Hindi.",
        },
        {
          question: "How much does EduWise cost?",
          answer: `EduWise plans are priced per child, per subject, per month. Starter: ₹${plans[0]?.price || 499}/mo, Plus: ₹${plans[1]?.price || 699}/mo, Pro: ₹${plans[2]?.price || 899}/mo. Plus, get 20% off for each additional child! All plans include a ${freeTrialDays}-day free trial.`,
        },
        {
          question: "What languages does EduWise support?",
          answer:
            "EduWise supports 11 Indian languages: English, Hindi, Tamil, Telugu, Kannada, Marathi, Punjabi, Bengali, Gujarati, Malayalam, and Odia.",
        },
      ]),
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <SEO
        title="EduWise - AI Tutor | Personalized Learning for Grades 5-8 | Eduzy"
        description={`Give your child personalized AI tutoring with EduWise. Adaptive learning, CBSE & IB curriculum-aligned, progress tracking, and parent dashboard. ${freeTrialDays}-day free trial. From ₹${plans[0]?.price || 499}/child/subject/month. 20% off for additional children!`}
        keywords="AI tutor, personalized learning, online tutoring, CBSE tutor, grades 5-8, adaptive learning, parent dashboard, progress tracking, Indian languages"
        canonicalUrl="/eduwise"
        structuredData={eduWiseStructuredData}
      />

      {/* Header */}
      <SharedHeader showNav={true} />

      {/* Hero Section */}
      <section className="relative py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-600 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-cyan-300/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/3 -left-20 w-72 h-72 bg-blue-300/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-1/4 w-56 h-56 bg-indigo-400/15 rounded-full blur-2xl"></div>
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white rounded-full px-4 py-1.5 mb-4 border border-white/30">
            <GraduationCap className="w-4 h-4" />
            <span className="font-medium text-sm">Personalized AI Learning</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Your Child&apos;s Personal
            <br />
            <span className="text-cyan-200">AI Tutor</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-6">
            Smart, conversational learning that adapts to your child&apos;s pace and style.
            Curriculum-aligned content for grades 5-8.
          </p>

          <div className="flex flex-col items-center justify-center">
            <Button
              onClick={() => navigate("/login?register=true")}
              size="lg"
              className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-full font-semibold shadow-xl"
              data-testid="eduwise-hero-cta"
            >
              Start {freeTrialDays}-Day Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-sm text-white/80 mt-4">No credit card required</p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
            How EduWise Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Adaptive Learning</h3>
              <p className="text-gray-600">
                AI analyzes your child&apos;s responses and adapts lessons in real-time.
                Struggling? More practice. Excelling? Move ahead.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Curriculum Aligned</h3>
              <p className="text-gray-600">
                Content follows CBSE and IB curricula for grades 5-8. Math, Science,
                English, and Hindi.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Progress Tracking</h3>
              <p className="text-gray-600">
                Detailed parent dashboard with insights on learning progress, strengths,
                and areas needing attention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-emerald-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Globe className="w-6 h-6 text-emerald-600" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              Learn in Your Language
            </h2>
          </div>
          <p className="text-gray-600 mb-4">Available in 11 Indian languages for better understanding</p>
          <div className="flex flex-wrap justify-center gap-2">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <Badge 
                key={lang} 
                variant="secondary" 
                className="bg-white text-gray-700 border border-emerald-200 px-3 py-1"
              >
                {lang}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8">
            Why Parents Choose EduWise
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: MessageCircle,
                title: "WhatsApp Learning",
                desc: "Learn anytime via WhatsApp - no app download needed.",
                bgColor: "bg-gradient-to-br from-emerald-50 to-teal-50",
                borderColor: "border-emerald-200",
                iconBg: "bg-emerald-500",
                iconColor: "text-white",
              },
              {
                icon: Sparkles,
                title: "AI-Powered Lessons",
                desc: "Natural, chat-based learning that feels like a friendly tutor.",
                bgColor: "bg-gradient-to-br from-violet-50 to-purple-50",
                borderColor: "border-violet-200",
                iconBg: "bg-violet-500",
                iconColor: "text-white",
              },
              {
                icon: Users,
                title: "Parent Dashboard",
                desc: "Track progress, view reports, and understand learning journey.",
                bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
                borderColor: "border-amber-200",
                iconBg: "bg-amber-500",
                iconColor: "text-white",
              },
              {
                icon: Shield,
                title: "DPDP Compliant",
                desc: "Full compliance with India's Data Protection guidelines.",
                bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
                borderColor: "border-blue-200",
                iconBg: "bg-blue-500",
                iconColor: "text-white",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className={`${feature.bgColor} rounded-xl p-6 border ${feature.borderColor} hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
              >
                <div className={`w-12 h-12 ${feature.iconBg} rounded-xl flex items-center justify-center mb-4 shadow-md`}>
                  <feature.icon className={`w-6 h-6 ${feature.iconColor}`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <Badge className="bg-emerald-100 text-emerald-700 border-0 mb-4">
              <Sparkles className="w-3 h-3 mr-1" />
              {freeTrialDays}-Day Free Trial on All Plans
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Choose Your Learning Plan
            </h2>
            <p className="text-gray-600 mb-2">
              All plans are <span className="font-semibold text-emerald-600">per child, per subject, per month</span>. Upgrade anytime.
            </p>
            <button
              onClick={generatePDF}
              disabled={generatingPdf}
              className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors disabled:opacity-50"
              data-testid="download-brochure-link"
            >
              {generatingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Download Complete Features Comparison (PDF)
                </>
              )}
            </button>
          </div>

          {/* Multi-Child Discount Banner */}
          <div className="max-w-2xl mx-auto bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-5 mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <Percent className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-emerald-800">Multi-Child Discount</h3>
            </div>
            <p className="text-emerald-700 font-medium mb-3 text-center">
              Get <span className="text-xl font-bold">20% OFF</span> for each additional child!
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Badge variant="outline" className="bg-white text-gray-700 border-gray-300 px-3 py-1">
                1st Child: Full Price
              </Badge>
              <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 px-3 py-1 font-semibold">
                2nd+ Child: 20% OFF
              </Badge>
            </div>
            <p className="text-xs text-emerald-600 mt-3 text-center">
              <UserPlus className="w-3 h-3 inline mr-1" />
              Add more children during registration to save!
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {plans.map((plan) => {
                const Icon = plan.icon;
                return (
                  <Card
                    key={plan.id}
                    className={`relative overflow-hidden border-2 transition-all duration-300 hover:shadow-xl flex flex-col ${
                      plan.popular
                        ? "border-violet-400 shadow-lg scale-105"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`eduwise-plan-${plan.id}`}
                  >
                    {plan.popular && (
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-violet-500 to-purple-600 text-white text-center py-1 text-sm font-medium">
                        Most Popular
                      </div>
                    )}

                    <CardContent className={`p-6 flex-1 flex flex-col ${plan.popular ? "pt-10" : ""}`}>
                      {/* Plan Header */}
                      <div className="text-center mb-6">
                        <div
                          className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${plan.bgGradient} flex items-center justify-center shadow-lg`}
                        >
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {plan.name}
                        </h3>
                        <p className="text-sm text-gray-500">{plan.description}</p>
                      </div>

                      {/* Price */}
                      <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-2xl font-medium text-gray-500">₹</span>
                          <span className="text-5xl font-bold text-gray-900">
                            {plan.price}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          per child / subject / month
                        </p>
                        <p className="text-xs text-orange-600 mt-1">
                          + 18% GST
                        </p>
                        <div className="flex flex-wrap justify-center gap-2 mt-3">
                          <Badge
                            variant="outline"
                            className="border-emerald-200 text-emerald-700"
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            {plan.dailyLimit}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="border-blue-200 text-blue-700"
                          >
                            <Users className="w-3 h-3 mr-1" />
                            20% off 2nd+ child
                          </Badge>
                        </div>
                      </div>

                      {/* Features */}
                      <ul className="space-y-3 mb-6 flex-1">
                        {plan.features.map((feature, idx) => (
                          <li key={`feature-${idx}`} className="flex items-start gap-3">
                            <div
                              className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                feature.included
                                  ? feature.highlight
                                    ? `bg-gradient-to-r ${plan.bgGradient}`
                                    : "bg-emerald-100"
                                  : "bg-gray-100"
                              }`}
                            >
                              {feature.included ? (
                                <Check
                                  className={`w-3 h-3 ${
                                    feature.highlight
                                      ? "text-white"
                                      : "text-emerald-600"
                                  }`}
                                />
                              ) : (
                                <X className="w-3 h-3 text-gray-400" />
                              )}
                            </div>
                            <span
                              className={`text-sm ${
                                feature.included
                                  ? feature.highlight
                                    ? "font-medium text-gray-900"
                                    : "text-gray-700"
                                  : "text-gray-400"
                              }`}
                            >
                              {feature.name}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <Button
                        onClick={() => handleSelectPlan(plan.id)}
                        variant={plan.ctaVariant}
                        className={`w-full mt-auto ${
                          plan.popular
                            ? "bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white border-0"
                            : ""
                        }`}
                        data-testid={`eduwise-select-${plan.id}`}
                      >
                        {plan.cta}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Give Your Child the AI Advantage
          </h2>
          <p className="text-blue-100 mb-6">
            Join thousands of families using EduWise for smarter, personalized learning.
          </p>
          <Button
            onClick={() => navigate("/login?register=true")}
            size="lg"
            className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-full font-semibold shadow-xl"
            data-testid="eduwise-bottom-cta"
          >
            Start {freeTrialDays}-Day Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}
